import SurveyService from '@/services/surveyService';
import {
  buildSurveyFreshnessSummary,
  latestTimestampFromRows,
  type SurveyFreshnessSummary,
  type SurveyFreshnessType,
} from '../utils/surveyFreshness';

const SURVEY_TYPES: SurveyFreshnessType[] = ['strategy', 'capabilities', 'readiness'];

function pickRows(payload: unknown): unknown[] {
  if (typeof SurveyService.pickResponses === 'function') {
    return SurveyService.pickResponses(payload) || [];
  }
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.responses)) return obj.responses;
    if (obj.data && typeof obj.data === 'object') {
      const data = obj.data as Record<string, unknown>;
      if (Array.isArray(data.responses)) return data.responses;
      if (Array.isArray(obj.data)) return obj.data as unknown[];
    }
  }
  return [];
}

/**
 * Load strategy / capabilities / readiness response timestamps for the Surveys card.
 * Uses existing survey response APIs (not Personalization Engine /run).
 */
export async function fetchSurveyFreshness(
  clientId: string,
  userId = 'admin',
): Promise<SurveyFreshnessSummary> {
  const results = await Promise.all(
    SURVEY_TYPES.map(async (type) => {
      try {
        const payload = await SurveyService.getSurveyResponses(clientId, type, {
          userId,
          // Always refresh — Personalize should reflect answers just saved in the modal.
          skipCache: true,
        });
        return { type, at: latestTimestampFromRows(pickRows(payload)) };
      } catch (err) {
        console.warn(`[surveyFreshness] Failed to load ${type} responses:`, err);
        return { type, at: null as Date | null };
      }
    }),
  );

  const timestamps = Object.fromEntries(results.map((r) => [r.type, r.at])) as Record<
    SurveyFreshnessType,
    Date | null
  >;

  return buildSurveyFreshnessSummary(timestamps);
}
